import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  User, 
  Mail, 
  Phone, 
  Palette, 
  Star, 
  Calendar,
  Award,
  Clock,
  AlertTriangle,
  Badge,
  Store
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { ConfirmInvitationModal } from '../components/ConfirmInvitationModal';
import { SuccessModal } from '../components/SuccessModal';
import { apiClient, getApiUrl } from '../utils/api';

interface ShopAssociation {
  id: string;
  shopId: string;
  shopName: string;
  shopCity: string;
  relationshipType: 'FREELANCE' | 'EMPLOYEE' | 'RENTAL' | 'PARTNERSHIP';
  hourlyRate?: number;
  commissionRate?: number;
  isActive: boolean;
  startDate: string;
  endDate?: string;
}

interface Practitioner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bio: string;
  experience: number;
  specialties: Array<{
    category: 'TATTOO' | 'PIERCING';
    styles: string[];
    experience: number;
    certifications: string[];
  }>;
  certifications: Array<{
    name: string;
    issuer: string;
    number: string;
    issuedAt: string;
    expiresAt?: string;
    verified: boolean;
  }>;
  portfolio: Array<{
    id: string;
    title: string;
    description: string;
    category: string;
    style: string;
    images: string[];
    featured: boolean;
    createdAt: string;
  }>;
  availability: {
    schedule: {
      monday?: { open: string; close: string };
      tuesday?: { open: string; close: string };
      wednesday?: { open: string; close: string };
      thursday?: { open: string; close: string };
      friday?: { open: string; close: string };
      saturday?: { open: string; close: string };
      sunday?: { open: string; close: string };
    };
    bookingWindow: number;
    minNotice: number;
  };
  pricing: {
    level: 'junior' | 'senior' | 'master' | 'guest';
    hourlyRate: number;
    minimumCharge: number;
    consultationFee: number;
  };
  rating: number;
  reviewCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  userId?: string | null;
}

export function PractitionerViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [associatedShops, setAssociatedShops] = useState<ShopAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationModal, setInvitationModal] = useState<'send' | 'link' | null>(null);
  const [invitationSending, setInvitationSending] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (id) {
      fetchPractitioner(id);
      fetchAssociatedShops(id);
    }
  }, [id]);

  const fetchPractitioner = async (practitionerId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/practitioners/${practitionerId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch practitioner');
      }

      const data = await response.json();
      if (data.success) {
        const practitionerData = data.data.practitioner;
        // Ensure arrays and nested objects are properly initialized
        setPractitioner({
          ...practitionerData,
          specialties: Array.isArray(practitionerData.specialties) 
            ? practitionerData.specialties.map((specialty: any) => ({
                category: specialty.category || 'TATTOO',
                styles: Array.isArray(specialty.styles) ? specialty.styles : [],
                experience: specialty.experience || 0,
                certifications: Array.isArray(specialty.certifications) ? specialty.certifications : []
              }))
            : [],
          certifications: Array.isArray(practitionerData.certifications) 
            ? practitionerData.certifications 
            : [],
          portfolio: Array.isArray(practitionerData.portfolio)
            ? practitionerData.portfolio
            : [],
          availability: {
            schedule: practitionerData.availability?.schedule || {},
            bookingWindow: practitionerData.availability?.bookingWindow || 90,
            minNotice: practitionerData.availability?.minNotice || 24
          },
          pricing: practitionerData.pricing || {
            level: 'junior',
            hourlyRate: 0,
            minimumCharge: 0,
            consultationFee: 0
          }
        });
      } else {
        setError(data.message || 'Failed to load practitioner');
      }
    } catch (error) {
      console.error('Fetch practitioner error:', error);
      setError('Failed to load practitioner data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociatedShops = async (practitionerId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/practitioners/${practitionerId}/shops`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAssociatedShops(data.data.shops || []);
        }
      }
    } catch (error) {
      console.error('Fetch associated shops error:', error);
    }
  };

  const handleEdit = () => {
    if (practitioner) {
      navigate(`/practitioners/${practitioner.id}/edit`);
    }
  };

  const handleSetPassword = async () => {
    if (!practitioner) return;
    const p1 = window.prompt(t('practitionerAccount.setPasswordPromptView'), '');
    if (!p1) return;
    const p2 = window.prompt(t('practitionerAccount.confirmPasswordPrompt'), '');
    if (!p2) return;
    if (p1 !== p2) {
      window.alert(t('practitionerAccount.passwordsMismatch'));
      return;
    }
    try {
      const resp = await apiClient(`/api/admin/practitioners/${practitioner.id}/password`, {
        method: 'PUT',
        body: JSON.stringify({ password: p1 }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || t('practitionerAccount.passwordUpdateError'));
      }
      window.alert(t('practitionerAccount.passwordUpdatedView'));
    } catch (e: any) {
      window.alert(e?.message || t('practitionerAccount.passwordUpdateErrorGeneric'));
    }
  };

  const handleSendInvitationClick = () => {
    setInvitationError(null);
    setInvitationModal('send');
  };

  const handleLinkAccountClick = () => {
    setInvitationError(null);
    setInvitationModal('link');
  };

  const handleInvitationConfirm = async () => {
    if (!practitioner) return;
    setInvitationSending(true);
    setInvitationError(null);
    try {
      const endpoint =
        invitationModal === 'link'
          ? `/api/admin/practitioners/${practitioner.id}/link-account`
          : `/api/admin/practitioners/${practitioner.id}/send-invitation`;
      const resp = await apiClient(endpoint, { method: 'POST' });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || t('common.error'));
      }
      setInvitationModal(null);
      const isLink = invitationModal === 'link';
      setSuccessModal({
        title: isLink ? t('practitionerInvitation.successModal.titleCreated') : t('practitionerInvitation.successModal.titleSent'),
        message: isLink ? t('practitionerInvitation.successModal.messageCreated') : t('practitionerInvitation.successModal.messageSent'),
      });
      if (invitationModal === 'link') {
        fetchPractitioner(practitioner.id);
      }
    } catch (e: any) {
      setInvitationError(e?.message || t('practitionerInvitation.error'));
    } finally {
      setInvitationSending(false);
    }
  };

  const closeInvitationModal = () => {
    if (!invitationSending) {
      setInvitationModal(null);
      setInvitationError(null);
    }
  };

  const handleBackToList = () => {
    navigate('/practitioners');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const getLevelBadge = (level: string) => {
    const levelConfig = {
      junior: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Junior' },
      senior: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Senior' },
      master: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Master' },
      guest: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Invité' }
    };
    
    const config = levelConfig[level as keyof typeof levelConfig] || levelConfig.junior;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const renderRating = (rating: number, reviewCount: number) => {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex items-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-5 h-5 ${
                star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
              }`}
            />
          ))}
        </div>
        <span className="text-lg font-medium">{rating.toFixed(1)}</span>
        <span className="text-gray-500">({reviewCount} avis)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement de l'artiste...</span>
      </div>
    );
  }

  if (error || !practitioner) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || t('navigation.practitioners.notFound')}
            </span>
          </div>
        </div>
        <button
          onClick={handleBackToList}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des artistes
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>{practitioner.firstName} {practitioner.lastName}</span>
              <HelpIcon 
                docPath="06-gestion-praticiens#06-4-consultation" 
                tooltip="Documentation : Consultation d'un praticien"
              />
            </h1>
            <p className="text-textSecondary">Profil de l'artiste</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!practitioner.userId ? (
            <button
              onClick={handleLinkAccountClick}
              className="btn-primary flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>{t('practitionerAccount.createAccount')}</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleSendInvitationClick}
                className="btn-secondary flex items-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>{t('practitionerAccount.sendInvitation')}</span>
              </button>
              <button
                onClick={handleSetPassword}
                className="btn-secondary flex items-center space-x-2"
              >
                <span>{t('practitionerAccount.setPassword')}</span>
              </button>
            </>
          )}
          <button
            onClick={handleEdit}
            className="btn-primary flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Modifier</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-8">
          {/* Personal Information */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Informations Personnelles</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{practitioner.email}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Téléphone</p>
                  <p className="font-medium">{practitioner.phone}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Expérience</p>
                  <p className="font-medium">{practitioner.experience} ans</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Badge className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <div className="mt-1">{getStatusBadge(practitioner.isActive)}</div>
                </div>
              </div>
            </div>

            {practitioner.bio && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Biographie</h3>
                <p className="text-gray-900 leading-relaxed">{practitioner.bio}</p>
              </div>
            )}
          </div>

          {/* Specialties */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                <Palette className="w-5 h-5 text-accent-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Spécialités</h2>
            </div>

            {practitioner.specialties.length > 0 ? (
              <div className="space-y-6">
                {practitioner.specialties.map((specialty, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-textPrimary">
                        {specialty.category === 'TATTOO' ? 'Tatouage' : 'Piercing'}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {specialty.experience} ans d'expérience
                      </span>
                    </div>

                    {specialty.styles.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Styles maîtrisés:</p>
                        <div className="flex flex-wrap gap-2">
                          {specialty.styles.map((style, styleIndex) => (
                            <span
                              key={styleIndex}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {style}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>Aucune spécialité renseignée</p>
              </div>
            )}
          </div>

          {/* Certifications */}
          {practitioner.certifications.length > 0 && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <Award className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Certifications</h2>
              </div>

              <div className="space-y-4">
                {practitioner.certifications.map((cert, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-textPrimary">{cert.name}</h3>
                        <p className="text-sm text-gray-600">{cert.issuer}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Émis le {formatDate(cert.issuedAt)}
                          {cert.expiresAt && ` • Expire le ${formatDate(cert.expiresAt)}`}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {cert.verified && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Vérifié
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Stats & Pricing */}
        <div className="space-y-8">
          {/* Statistics */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Évaluations</h2>
            </div>

            <div className="text-center">
              {renderRating(practitioner.rating, practitioner.reviewCount)}
            </div>
          </div>

          {/* Pricing */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                <Star className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Tarification</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Niveau</span>
                {getLevelBadge(practitioner.pricing.level)}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tarif horaire</span>
                <span className="font-medium">{practitioner.pricing.hourlyRate}€/h</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tarif minimum</span>
                <span className="font-medium">{practitioner.pricing.minimumCharge}€</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Consultation</span>
                <span className="font-medium">{practitioner.pricing.consultationFee}€</span>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Disponibilités</h2>
            </div>

            <div className="space-y-3">
              {Object.entries(practitioner.availability.schedule).map(([day, hours]) => {
                if (!hours) return null;
                
                const dayNames = {
                  monday: 'Lundi',
                  tuesday: 'Mardi',
                  wednesday: 'Mercredi',
                  thursday: 'Jeudi',
                  friday: 'Vendredi',
                  saturday: 'Samedi',
                  sunday: 'Dimanche'
                };
                
                return (
                  <div key={day} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{dayNames[day as keyof typeof dayNames]}</span>
                    <span className="font-medium">{hours.open} - {hours.close}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Réservation à l'avance</span>
                <span className="font-medium">{practitioner.availability.bookingWindow} jours</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Préavis minimum</span>
                <span className="font-medium">{practitioner.availability.minNotice}h</span>
              </div>
            </div>
          </div>

          {/* Shops Associations */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                <Store className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Shops Associés</h2>
            </div>

            {associatedShops.length > 0 ? (
              <div className="space-y-3">
                {associatedShops.map((association) => (
                  <div key={association.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-textPrimary">{association.shopName}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          association.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {association.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {association.relationshipType === 'FREELANCE' && 'Freelance'}
                          {association.relationshipType === 'EMPLOYEE' && 'Employé'}
                          {association.relationshipType === 'RENTAL' && 'Location'}
                          {association.relationshipType === 'PARTNERSHIP' && 'Partenariat'}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{association.shopCity}</p>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {association.hourlyRate && (
                        <div>
                          <span className="text-gray-500">Tarif:</span>
                          <span className="ml-1 font-medium">{association.hourlyRate}€/h</span>
                        </div>
                      )}
                      {association.commissionRate && (
                        <div>
                          <span className="text-gray-500">Commission:</span>
                          <span className="ml-1 font-medium">{association.commissionRate}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Aucun shop associé</p>
            )}
          </div>

          {/* Account Info */}
          <div className="card">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Informations du compte</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Créé le</span>
                <span className="font-medium">{formatDate(practitioner.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Dernière modification</span>
                <span className="font-medium">{formatDate(practitioner.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Send Invitation / Link Account Modal */}
      {practitioner && invitationModal && (
        <ConfirmInvitationModal
          isOpen={!!invitationModal}
          onClose={closeInvitationModal}
          onConfirm={handleInvitationConfirm}
          practitionerName={`${practitioner.firstName} ${practitioner.lastName}`}
          practitionerEmail={practitioner.email}
          isLoading={invitationSending}
          isLinkAccount={invitationModal === 'link'}
          error={invitationError}
        />
      )}

      {/* Success Modal */}
      {successModal && (
        <SuccessModal
          isOpen={!!successModal}
          onClose={() => setSuccessModal(null)}
          title={successModal.title}
          message={successModal.message}
        />
      )}
    </div>
  );
}